import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  AlertCircle,
  CheckCircle,
  Image,
  Loader2,
  Plus,
  RefreshCw,
  Upload,
  X,
} from "lucide-react";

import { Button } from "~/components/ui/button";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";

interface ProofOfDeliveryDocument {
  id: string;
  sales_request_id: string;
  document_type: string;
  document_url: string;
  filename: string;
  content_type: string;
  uploaded_by: string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;
  is_approved: boolean;
}

interface FileUploadStatus {
  file: File;
  preview: string;
  status: "pending" | "uploading" | "success" | "failed";
  error?: string;
  documentId?: string;
}

interface ProofOfDeliverySectionProps {
  selectedOrder: SalesRequest;
  onOrderStatusChange?: () => void;
}

export const ProofOfDeliverySection: React.FC<ProofOfDeliverySectionProps> = ({
  selectedOrder,
  onOrderStatusChange,
}) => {
  const [deliveryProofs, setDeliveryProofs] = useState<
    ProofOfDeliveryDocument[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);
  const [approving, setApproving] = useState<string | null>(null);

  // Track mounted state to prevent state updates after unmount (fixes NotFoundError on iOS)
  const isMountedRef = useRef(true);

  // Hidden file input driven by the custom dropzone below.
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const profileStore = useProfileStore();
  const isAdmin =
    profileStore.role === "superadmin" || profileStore.role === "admin";
  const isLogistica = profileStore.role === "logistica";
  const isFacturacion = profileStore.role === "facturacion";
  const isTransportista = profileStore.role === "transportista";
  const isAssignedTransportista =
    isTransportista && selectedOrder.assigned_transportista === profileStore.id;

  // Retry utility with exponential backoff for network failures
  const retryWithBackoff = async <T,>(
    fn: () => Promise<T>,
    maxAttempts = 3,
    baseDelayMs = 1000
  ): Promise<T> => {
    let lastError: Error | null = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error as Error;
        if (attempt < maxAttempts) {
          const delay = baseDelayMs * Math.pow(2, attempt - 1);
          toast({
            title: "Conexión inestable",
            description: `Reintentando en ${delay / 1000}s... (intento ${attempt}/${maxAttempts})`,
            variant: "default",
          });
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }
    throw lastError;
  };

  // Image compression utility
  const compressImage = (
    file: File,
    quality = 0.8,
    maxWidth = 1920,
    maxHeight = 1080
  ): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = document.createElement("img");

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              resolve(file); // Fallback to original if compression fails
            }
          },
          file.type,
          quality
        );
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // All users can see the proof of delivery section when status is in_transit or delivered
  // Only admins and assigned transportistas can upload (only for in_transit orders)
  const canUpload =
    selectedOrder.status === "in_transit" &&
    (isAdmin || isAssignedTransportista);
  const canApprove =
    (isAdmin || isLogistica || isFacturacion) &&
    (selectedOrder.status === "in_transit" ||
      selectedOrder.status === "delivered");

  // Fetch existing delivery proofs
  const fetchDeliveryProofs = useCallback(async () => {
    if (!selectedOrder.id || !isMountedRef.current) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("sales_request_documents")
        .select("*")
        .eq("sales_request_id", selectedOrder.id)
        .eq("document_type", "proof_of_delivery")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Only update state if still mounted
      if (isMountedRef.current) {
        setDeliveryProofs(data || []);
      }
    } catch (error) {
      console.error("Error fetching delivery proofs:", error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [selectedOrder.id]);

  useEffect(() => {
    fetchDeliveryProofs();
  }, [fetchDeliveryProofs]);

  // Helper to create preview URL for a file
  const createPreview = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
  };

  // Handle file selection (multiple files)
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const newFileStatuses: FileUploadStatus[] = [];

    // Process files with potential compression
    for (const file of selectedFiles) {
      // Validate file type (only images)
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Tipo de archivo no válido",
          description: `${file.name}: Solo se permiten archivos de imagen (PNG, JPG, JPEG, etc.)`,
          variant: "destructive",
        });
        continue;
      }

      let processedFile = file;

      // If file is larger than 5MB, attempt compression
      if (file.size > 5 * 1024 * 1024) {
        try {
          toast({
            title: "Comprimiendo imagen",
            description: `${file.name}: Archivo muy grande, comprimiendo automáticamente...`,
            variant: "default",
          });

          processedFile = await compressImage(file);

          // Check if compressed file is still too large
          if (processedFile.size > 5 * 1024 * 1024) {
            // Try with lower quality
            processedFile = await compressImage(file, 0.5);
          }

          // If still too large after compression, reject it
          if (processedFile.size > 5 * 1024 * 1024) {
            toast({
              title: "Archivo muy grande",
              description: `${file.name}: Incluso después de compresión, el archivo sigue siendo muy grande (>5MB)`,
              variant: "destructive",
            });
            continue;
          } else {
            toast({
              title: "Imagen comprimida",
              description: `${file.name}: Comprimido de ${(file.size / 1024 / 1024).toFixed(1)}MB a ${(processedFile.size / 1024 / 1024).toFixed(1)}MB`,
              variant: "default",
            });
          }
        } catch (error) {
          console.log("error is", error);
          toast({
            title: "Error al comprimir",
            description: `${file.name}: No se pudo comprimir la imagen`,
            variant: "destructive",
          });
          continue;
        }
      }

      // Create preview and add to new statuses
      const preview = await createPreview(processedFile);
      newFileStatuses.push({
        file: processedFile,
        preview,
        status: "pending",
      });
    }

    if (newFileStatuses.length > 0) {
      // Add to existing file statuses (only pending ones, keep successful uploads)
      setFileStatuses((prev) => [...prev, ...newFileStatuses]);
    }

    // Clear the input to allow selecting the same files again if needed
    e.target.value = "";
  };

  // Remove a specific file from the selection (only pending or failed files)
  const removeFile = (index: number) => {
    setFileStatuses((prev) => prev.filter((_, i) => i !== index));
  };

  // Upload a single file with status tracking
  const uploadSingleFile = async (
    fileStatus: FileUploadStatus,
    index: number
  ): Promise<{ success: boolean; documentId?: string; error?: string }> => {
    const { file } = fileStatus;

    try {
      // Upload file to Supabase Storage with retry logic
      const fileExt = file.name.split(".").pop();
      const fileName = `${selectedOrder.id}_${Date.now()}_${index}.${fileExt}`;

      const { error: uploadError } = await retryWithBackoff(() =>
        supabase.storage
          .from("sales-request-documents")
          .upload(`proof-of-delivery/${fileName}`, file)
      );

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("sales-request-documents")
        .getPublicUrl(`proof-of-delivery/${fileName}`);

      // Save document record
      // Auto-approve for all users - any upload should mark as delivered
      const isAutoApproved = true;
      const documentRecord = {
        sales_request_id: selectedOrder.id,
        document_type: "proof_of_delivery",
        document_url: urlData.publicUrl,
        filename: file.name,
        content_type: file.type,
        uploaded_by: profileStore.id,
        is_approved: isAutoApproved,
        approved_by: isAutoApproved ? profileStore.id : null,
        approved_at: isAutoApproved ? new Date().toISOString() : null,
      };

      const { data, error: insertError } = await supabase
        .from("sales_request_documents")
        .insert(documentRecord as any)
        .select("id")
        .single();

      if (insertError) throw insertError;

      return { success: true, documentId: data?.id };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error desconocido";
      return { success: false, error: errorMessage };
    }
  };

  // Upload proof of delivery (multiple files) - sequential processing
  const handleUpload = async () => {
    const pendingFiles = fileStatuses.filter(
      (fs) => fs.status === "pending" || fs.status === "failed"
    );
    if (pendingFiles.length === 0) return;

    setUploading(true);

    let successCount = 0;
    let failCount = 0;

    // Process files sequentially
    for (let i = 0; i < fileStatuses.length; i++) {
      const fileStatus = fileStatuses[i];

      // Skip already successful uploads
      if (fileStatus.status === "success") {
        successCount++;
        continue;
      }

      // Update status to uploading
      if (isMountedRef.current) {
        setFileStatuses((prev) =>
          prev.map((fs, idx) =>
            idx === i ? { ...fs, status: "uploading" as const } : fs
          )
        );
      }

      // Upload the file
      const result = await uploadSingleFile(fileStatus, i);

      // Update status based on result
      if (isMountedRef.current) {
        setFileStatuses((prev) =>
          prev.map((fs, idx) =>
            idx === i
              ? {
                  ...fs,
                  status: result.success
                    ? ("success" as const)
                    : ("failed" as const),
                  documentId: result.documentId,
                  error: result.error,
                }
              : fs
          )
        );
      }

      if (result.success) {
        successCount++;
      } else {
        failCount++;
      }
    }

    // Only mark as delivered if ALL files uploaded successfully
    const allSucceeded = failCount === 0 && successCount > 0;

    if (allSucceeded) {
      // Update sales request status to delivered
      const { error: statusError } = await supabase
        .from("sales_request")
        .update({
          status: "delivered",
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (!statusError) {
        // Status history is recorded by the record_sales_request_status_change
        // trigger on the UPDATE above (no manual insert -> no duplicate row).

        // Refresh order status
        if (onOrderStatusChange) onOrderStatusChange();
      }
    }

    // Show appropriate toast message
    if (isMountedRef.current) {
      if (allSucceeded) {
        toast({
          title: "Entrega confirmada",
          description: `${successCount} comprobante(s) de entrega subido(s) y orden marcada como entregada.`,
          variant: "default",
        });

        // Clear successful uploads after a short delay
        setTimeout(() => {
          if (isMountedRef.current) {
            setFileStatuses([]);
            fetchDeliveryProofs();
          }
        }, 1500);
      } else if (successCount > 0 && failCount > 0) {
        toast({
          title: "Subida parcial",
          description: `${successCount} archivo(s) subido(s), ${failCount} fallido(s). Puede reintentar los archivos fallidos.`,
          variant: "destructive",
        });
        fetchDeliveryProofs();
      } else {
        toast({
          title: "Error de subida",
          description:
            "No se pudieron subir los archivos. Verifique su conexión e intente nuevamente.",
          variant: "destructive",
        });
      }
    }

    if (isMountedRef.current) {
      setUploading(false);
    }
  };

  // Retry failed uploads
  const handleRetryFailed = async () => {
    // Reset failed statuses to pending
    setFileStatuses((prev) =>
      prev.map((fs) =>
        fs.status === "failed"
          ? { ...fs, status: "pending" as const, error: undefined }
          : fs
      )
    );
    // Then trigger upload
    setTimeout(() => handleUpload(), 100);
  };

  // Approve proof of delivery
  const handleApprove = async (proofId: string) => {
    setApproving(proofId);
    try {
      // Update document as approved
      const { error: updateError } = await supabase
        .from("sales_request_documents")
        .update({
          is_approved: true,
          approved_by: profileStore.id,
          approved_at: new Date().toISOString(),
        } as any)
        .eq("id", proofId);

      if (updateError) throw updateError;

      // Update sales request status to delivered
      const { error: statusError } = await supabase
        .from("sales_request")
        .update({
          status: "delivered",
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (statusError) throw statusError;

      // Status history is recorded by the record_sales_request_status_change
      // trigger on the UPDATE above (no manual insert -> no duplicate row).

      // Only update UI state if still mounted
      if (isMountedRef.current) {
        toast({
          title: "Entrega aprobada",
          description:
            "El comprobante ha sido aprobado y la orden marcada como entregada.",
          variant: "default",
        });

        // Refresh order status
        if (onOrderStatusChange) onOrderStatusChange();

        // Refresh proofs
        fetchDeliveryProofs();
      }
    } catch (error) {
      console.error("Error approving proof:", error);
      if (isMountedRef.current) {
        toast({
          title: "Error",
          description: "No se pudo aprobar el comprobante de entrega",
          variant: "destructive",
        });
      }
    } finally {
      if (isMountedRef.current) {
        setApproving(null);
      }
    }
  };

  // Show for in_transit and delivered orders (users should be able to view proofs even after delivery)
  if (
    selectedOrder.status !== "in_transit" &&
    selectedOrder.status !== "delivered"
  ) {
    return null;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-100 px-5 py-3">
        <h3 className="flex items-center text-lg font-semibold text-gray-800">
          <Image className="mr-2 h-5 w-5 text-gray-600" />
          Comprobante de Entrega
        </h3>
      </div>

      <div className="p-5">
        <p className="mb-4 text-sm text-gray-600">
          Suba una o más fotos del comprobante de entrega. La orden se marcará
          como entregada automáticamente.
        </p>
        {/* Upload Form - Only show if no approved proof exists and user can upload */}
        {canUpload && !deliveryProofs.some((proof) => proof.is_approved) && (
          <div className="mb-4 space-y-4">
            {/* Hidden native input; the dashed area below is the trigger */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              disabled={uploading}
              className="hidden"
            />

            {fileStatuses.length === 0 ? (
              /* Empty state: the whole dashed area is a centered, clickable dropzone */
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 px-4 py-10 text-center transition-colors hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <Upload className="h-5 w-5" />
                </span>
                <span className="text-sm font-medium text-gray-700">
                  Seleccionar imágenes
                </span>
                <span className="text-xs text-muted-foreground">
                  Haz clic para elegir una o más fotos del comprobante
                </span>
              </button>
            ) : (
              <div className="space-y-4 rounded-lg border-2 border-dashed border-gray-300 p-4">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  {fileStatuses.map((fileStatus, index) => (
                    <div key={index} className="relative">
                      <img
                        src={fileStatus.preview}
                        alt={`Vista previa ${index + 1}`}
                        className={`h-24 w-full rounded object-cover ${
                          fileStatus.status === "failed"
                            ? "opacity-50 ring-2 ring-red-500"
                            : fileStatus.status === "success"
                              ? "ring-2 ring-green-500"
                              : fileStatus.status === "uploading"
                                ? "opacity-75"
                                : ""
                        }`}
                      />
                      {/* Status overlay */}
                      {fileStatus.status === "uploading" && (
                        <div className="absolute inset-0 flex items-center justify-center rounded bg-black/30">
                          <Loader2 className="h-6 w-6 animate-spin text-white" />
                        </div>
                      )}
                      {fileStatus.status === "success" && (
                        <div className="absolute left-1 top-1 rounded-full bg-green-500 p-0.5">
                          <CheckCircle className="h-4 w-4 text-white" />
                        </div>
                      )}
                      {fileStatus.status === "failed" && (
                        <div className="absolute left-1 top-1 rounded-full bg-red-500 p-0.5">
                          <AlertCircle className="h-4 w-4 text-white" />
                        </div>
                      )}
                      {/* Remove button - only for pending/failed */}
                      {(fileStatus.status === "pending" ||
                        fileStatus.status === "failed") && (
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute right-1 top-1 h-6 w-6 p-0"
                          onClick={() => removeFile(index)}
                          disabled={uploading}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                      <div className="absolute bottom-1 left-1 max-w-[calc(100%-8px)] truncate rounded bg-black/50 px-1 text-xs text-white">
                        {fileStatus.file.name.slice(0, 15)}
                        {fileStatus.file.name.length > 15 ? "..." : ""}
                      </div>
                    </div>
                  ))}

                  {/* Add-more tile */}
                  {!uploading && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex h-24 w-full flex-col items-center justify-center gap-1 rounded border-2 border-dashed border-gray-300 text-gray-400 transition-colors hover:border-gray-400 hover:bg-gray-50 hover:text-gray-500"
                    >
                      <Plus className="h-5 w-5" />
                      <span className="text-xs font-medium">Agregar</span>
                    </button>
                  )}
                </div>
                {/* Upload progress summary */}
                {uploading && (
                  <div className="text-center text-sm text-muted-foreground">
                    Subiendo{" "}
                    {fileStatuses.filter((fs) => fs.status === "uploading")
                      .length > 0
                      ? "archivo "
                      : ""}
                    {fileStatuses.findIndex((fs) => fs.status === "uploading") +
                      1}{" "}
                    de {fileStatuses.length}...
                  </div>
                )}

                {/* Status summary when not uploading */}
                {!uploading &&
                  fileStatuses.some((fs) => fs.status === "failed") && (
                    <div className="rounded-md bg-red-50 p-2 text-center text-sm text-red-700">
                      {
                        fileStatuses.filter((fs) => fs.status === "failed")
                          .length
                      }{" "}
                      archivo(s) fallido(s).
                      {fileStatuses.filter((fs) => fs.status === "success")
                        .length > 0 &&
                        ` ${fileStatuses.filter((fs) => fs.status === "success").length} subido(s) correctamente.`}
                    </div>
                  )}

                <div className="flex gap-2">
                  {/* Retry button for failed uploads */}
                  {!uploading &&
                    fileStatuses.some((fs) => fs.status === "failed") && (
                      <Button
                        onClick={handleRetryFailed}
                        variant="outline"
                        className="flex-1"
                      >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Reintentar fallidos
                      </Button>
                    )}

                  {/* Main upload button */}
                  <Button
                    onClick={handleUpload}
                    disabled={
                      uploading ||
                      !fileStatuses.some(
                        (fs) =>
                          fs.status === "pending" || fs.status === "failed"
                      )
                    }
                    className={
                      fileStatuses.some((fs) => fs.status === "failed") &&
                      !uploading
                        ? "flex-1"
                        : "w-full"
                    }
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        {`Confirmar Entrega (${fileStatuses.filter((fs) => fs.status === "pending" || fs.status === "failed").length} archivo${fileStatuses.filter((fs) => fs.status === "pending" || fs.status === "failed").length !== 1 ? "s" : ""})`}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Existing Proofs */}
        {loading ? (
          <div className="py-8 text-center text-gray-500">
            <div className="mb-2 inline-block h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
            <p>Cargando comprobantes...</p>
          </div>
        ) : deliveryProofs.length > 0 ? (
          <div className="space-y-3">
            <h4 className="font-medium">Comprobantes Subidos</h4>
            {deliveryProofs.map((proof) => (
              <div
                key={proof.id}
                className="flex items-center justify-between rounded-md border p-3"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={proof.document_url}
                    alt="Comprobante"
                    className="h-12 w-12 cursor-pointer rounded object-cover"
                    onClick={() => window.open(proof.document_url, "_blank")}
                  />
                  <div>
                    <p className="font-medium">{proof.filename}</p>
                    <p className="text-sm text-muted-foreground">
                      Subido el{" "}
                      {new Date(proof.created_at).toLocaleDateString("es-CL")}
                    </p>
                    {proof.is_approved && (
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        <span className="text-sm">Aprobado</span>
                      </div>
                    )}
                  </div>
                </div>

                {!proof.is_approved && canApprove && (
                  <Button
                    size="sm"
                    onClick={() => handleApprove(proof.id)}
                    disabled={approving === proof.id}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {approving === proof.id ? (
                      <>
                        <span className="mr-2 animate-spin">⏳</span>
                        Aprobando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Aprobar Entrega
                      </>
                    )}
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">
            <Image className="mx-auto mb-2 h-12 w-12 text-gray-400" />
            <p>No hay comprobantes de entrega subidos</p>
          </div>
        )}
      </div>
    </div>
  );
};
